import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, combineLatest, startWith, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { MODERATED_CHAT } from 'entity/SocialSettings';
import User from 'entity/User';
import {
  ChatResultMessage,
  ChatMessage,
  IncomingMessage,
  IncomingMessageType,
  ChatHistoryMessage,
  ChatStatus,
} from 'messages/websocket.messages';
import UserController from 'rest/UserController';

import { HyperlinkHelper } from '../../../utils/hyperlink-helper';
import SocialChatMessage from '../chat/social-chat-message/social-chat-message';
import SocialChatMessageState from '../chat/social-chat-message/social-chat-message-type';
import { SideSectionService } from '../services/side-section.service';
import { CHAT_SUB_MENU } from '../social-menu/social-menu.types';
import { ScootaverseUserState, socialAppActions, socialAppSelectors } from '../state/app';

@Injectable({
  providedIn: 'root',
})
export class ChatService extends Loggable implements OnDestroy {
  private sideSectionService = inject(SideSectionService);
  private _toasterService = inject(ToastrService);
  private store = inject<Store<AppState>>(Store);

  public readonly feed = signal<SocialChatMessage[]>([]);
  public readonly hostFeed = signal<SocialChatMessage[]>([]);
  public readonly moderationFeed = signal<SocialChatMessage[]>([]);
  public chatReceiver = new ReplaySubject<string>(1);
  public readonly unreadMessagesCount = signal(0);
  public userIdToReply?: string;
  public repliedMsg?: SocialChatMessage;
  public moderationChatHistory?: boolean;

  private readonly userList = signal<ScootaverseUserState>({});
  private readonly user = signal<User | undefined>(undefined);
  private isOpen = false;
  private lastMessageSentByMeIndex?: number;
  private userController = new UserController();

  private destroyed$ = new ReplaySubject<boolean>(1);
  private moderatedChat = false;
  private chatModerationSetting = false;
  private hostIdList?: string[];

  public constructor() {
    super();
    this.setDisplayName('ChatService');
    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => this.user.set(user));

    this.store
      .select(socialAppSelectors.isChatEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => {
        if (!enabled) {
          this.chatReceiver.next('all');
        }
        this.isOpen = enabled;
        this.resetWindow();
      });

    combineLatest([
      getFeatureFlagObservable(this.store, LaunchDarklyFlags.moderated_chat),
      this.store.select(socialAppSelectors.getRoomSettings),
      this.store.select(socialAppSelectors.getHostIdList),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([moderatedChat, socialSettings, hostIdList]) => {
        this.moderatedChat = moderatedChat;
        this.hostIdList = hostIdList;

        if (moderatedChat && socialSettings && socialSettings.setSettings) {
          this.chatModerationSetting = socialSettings.setSettings[MODERATED_CHAT] ? true : false;
        }
      });

    this.store
      .select(socialAppSelectors.getScootaverseUsers)
      .pipe(startWith({}), takeUntil(this.destroyed$))
      .subscribe((scootaverseUsers) => {
        this.userList.set(scootaverseUsers);
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public resetWindow(): void {
    if (this.isOpen) {
      this.unreadMessagesCount.set(0);
    }
  }

  public triggerPrivateMessage(id: string): void {
    if (!this.isOpen) {
      this.sideSectionService.openSubMenu(CHAT_SUB_MENU);
      this.resetWindow();
    }
    this.chatReceiver.next(id);
  }

  public toggleTriggerPrivateMessageBadge(id: string, fromChatBadge?: boolean): void {
    if (fromChatBadge) {
      this.chatReceiver.next(id);
    } else {
      this.logUserAction('Multimedia Chat Button', `toggled ${this.isOpen ? 'closed' : 'opened'}`);
      if (!this.isOpen) {
        this.sideSectionService.openSubMenu(CHAT_SUB_MENU);
        this.chatReceiver.next(id);
      } else {
        this.sideSectionService.popSubMenu();
        this.store.dispatch(socialAppActions.TOGGLE_CHAT({ enabled: false }));
      }
    }

    this.resetWindow();
  }

  public wsMessageReceived(msg: IncomingMessage): void {
    const { t } = msg;
    switch (t) {
      case IncomingMessageType.CHATMSG:
        void this.chatMessageReceived(msg);
        break;
      case IncomingMessageType.CHATRES:
        this.statusResponseOfASentMessage(msg);
        break;
      case IncomingMessageType.CHATHIST:
        this.chatHistoryReceived(msg);
        break;
    }
  }

  public addMessageISent(message: string, privacy: string, userIds: string | string[], sentTimestamp: number): void {
    if (!this.user()) {
      return;
    }

    // Build Message
    const scMessage: SocialChatMessage = {
      message,
      sender: this.user()!,
      sentDate: new Date(),
      privacy,
      messageState: SocialChatMessageState.READ,
      sentByMe: true,
      sentTimestamp,
    };
    // When direct message
    if (userIds !== 'all' && userIds !== 'all-rooms') {
      const receiverUser = this.getParticipantUser(userIds[0]);

      if (receiverUser) {
        scMessage.receiver = receiverUser;
      }
    }
    scMessage.message = HyperlinkHelper.formatMessage(scMessage.message);

    if (userIds === 'all' || userIds === 'all-rooms') {
      scMessage.sendedAsHost = this.isUserHost(this.user()?.userId ?? '');
    }

    if (scMessage.privacy === 'hosts') {
      this.hostFeed.update((feed) => {
        const newFeed = [...feed, scMessage];
        this.lastMessageSentByMeIndex = newFeed.length - 1;
        return newFeed;
      });
    } else {
      this.feed.update((feed) => {
        const newFeed = [...feed, scMessage];
        this.lastMessageSentByMeIndex = newFeed.length - 1;
        return newFeed;
      });
    }
  }

  private getParticipantUser(userId: string): User | undefined {
    const participant = this.userList()[userId];
    return participant ? participant : undefined;
  }

  private async chatMessageReceived({
    fromId,
    moderatorId,
    privacy,
    sentDate,
    sourceDate,
    status,
    text: message,
  }: ChatMessage): Promise<void> {
    // Find sender of the message
    let userInfo;
    const senderParticipant = this.getParticipantUser(fromId);
    if (!senderParticipant) {
      this.info('Social Chat Message received but user does not exist in participants map');
      userInfo = await this.userController.getUser(fromId);
    } else {
      userInfo = senderParticipant;
    }

    // Build Message
    const scMessage: SocialChatMessage = {
      message,
      sender: userInfo,
      sentDate: new Date(sentDate),
      sourceDate,
      privacy,
      messageState: this.isOpen ? SocialChatMessageState.READ : SocialChatMessageState.RECEIVED,
      sentByMe: this.user()?.userId === fromId,
      sentTimestamp: sentDate,
    };
    // Attach to feed
    scMessage.message = HyperlinkHelper.formatMessage(scMessage.message);

    if (status) {
      scMessage.status = status;
    }

    if (moderatorId) {
      const moderatorUser = this.getParticipantUser(moderatorId);
      let moderatorUserInfo;
      if (!moderatorUser) {
        this.info('Moderator Social Chat Message received but user does not exist in participants map');
        moderatorUserInfo = await this.userController.getUser(moderatorId);
      } else {
        moderatorUserInfo = moderatorUser;
      }

      if (!moderatorUserInfo) {
        this.error('Moderator user not found');
        return;
      }

      scMessage.moderator = moderatorUserInfo;
    }

    // Try to prevent message being duplicated in main feed
    if (this.hasMessageAlready(scMessage.message, this.feed, scMessage.sentTimestamp, scMessage.sender.userId)) {
      return;
    }

    if (scMessage.privacy === 'hosts') {
      this.hostFeed.update((feed) => [...feed, scMessage]);
    } else if (this.moderatedChat && this.chatModerationSetting && (!this.isUserHost(fromId) || scMessage.status)) {
      this.handleModeratedChat(scMessage);
    } else {
      this.feed.update((feed) => [...feed, scMessage]);
    }

    if (scMessage.privacy === 'hosts') {
      if (this.isUserHost(this.user()?.userId ?? '')) {
        this.unreadMessagesCount.set(this.unreadMessagesCount() + 1);
      }
    } else if (!this.isOpen) {
      this.unreadMessagesCount.set(this.unreadMessagesCount() + 1);
    }
  }

  private statusResponseOfASentMessage({ notSentTo }: ChatResultMessage): void {
    if (notSentTo.length > 0 && this.lastMessageSentByMeIndex !== undefined) {
      const currentFeed = this.feed();
      const myMessage = currentFeed[this.lastMessageSentByMeIndex];
      if (!myMessage) {
        return;
      }

      // Create a new updated message with the failed state
      const updatedMessage = {
        ...myMessage,
        messageState: SocialChatMessageState.FAILED,
      };

      // Update the feed with the new message
      this.feed.update((feed) => {
        const newFeed = [...feed];
        newFeed[this.lastMessageSentByMeIndex!] = updatedMessage;
        return newFeed;
      });
    }
  }

  private chatHistoryReceived(msg: ChatHistoryMessage) {
    const latestMessages = msg.messages;

    const insertMessageOnFeed = async (chatMessages: ChatMessage[]) => {
      const currentMessage = chatMessages.pop();
      if (currentMessage) {
        try {
          const user = await this.userController.getUser(currentMessage.fromId);
          if (user) {
            const scMessage: SocialChatMessage = {
              message: currentMessage.text,
              sender: user,
              sentDate: new Date(currentMessage.sentDate),
              sourceDate: currentMessage.sourceDate,
              privacy: currentMessage.privacy,
              messageState: this.isOpen ? SocialChatMessageState.READ : SocialChatMessageState.RECEIVED,
              sentByMe: this.user()?.userId === currentMessage.fromId,
              sentTimestamp: currentMessage.sentDate,
            };

            if (currentMessage.status) {
              scMessage.status = currentMessage.status;
            }

            if (currentMessage.moderatorId) {
              scMessage.moderator = await this.userController.getUser(currentMessage.moderatorId);
            }

            scMessage.message = HyperlinkHelper.formatMessage(scMessage.message);

            if (this.moderationChatHistory && this.moderatedChat && this.chatModerationSetting) {
              this.handleModeratedChat(scMessage, 'history');
            } else {
              if (scMessage.privacy === 'hosts') {
                this.hostFeed.update((feed) => [...feed, scMessage]);
              } else if (this.moderatedChat && this.chatModerationSetting) {
                this.handleModeratedChat(scMessage, 'history');
              } else {
                this.feed.update((feed) => [...feed, scMessage]);
              }
            }
          }
        } catch (err) {
          this.error('Error while getting the user', err);
        } finally {
          await insertMessageOnFeed(latestMessages);
        }
      }
    };
    insertMessageOnFeed(latestMessages).catch((err) => this.error(err));
  }

  public clear(): void {
    this.feed.set([]);
    this.lastMessageSentByMeIndex = undefined;
    this.unreadMessagesCount.set(0);
    this.moderationFeed.set([]);
    this.hostFeed.set([]);
  }

  private handleModeratedChat(scMessage: SocialChatMessage, origin?: string): void {
    if (
      origin === 'history' &&
      this.isUserHost(this.user()?.userId ?? '') &&
      scMessage.status &&
      !this.hasMessageAlready(scMessage.message, this.moderationFeed, scMessage.sourceDate, scMessage.sender.userId)
    ) {
      this.moderationFeed.update((feed) => [...feed, scMessage]);
    }

    // If message is pending, only moderation feed is update
    if (scMessage.status === ChatStatus.PENDING && origin !== 'history') {
      this.moderationFeed.update((feed) => [...feed, scMessage]);
    } else if (scMessage) {
      // Update moderation feed
      if (scMessage.status) {
        this.updateMessageFeed(scMessage, this.moderationFeed);

        if (scMessage.status === ChatStatus.REPLIED) {
          this.updateModerationFeedOnReply(scMessage);
        }
      }

      if (scMessage.sentByMe) {
        // Update main feed of the user that sended the message
        if (scMessage.status) {
          this.updateMessageFeed(scMessage, this.feed);

          if (scMessage.status === ChatStatus.REPLIED) {
            this.updateMainFeedOnSendByMe(scMessage, this.feed);
          }
        }

        if (origin !== 'history') {
          if (scMessage.status === ChatStatus.REJECTED) {
            this._toasterService.error('Hosted Dismissed your message.', 'Message Dismissed');
          } else if (scMessage.status === ChatStatus.APPROVED) {
            this._toasterService.success('Hosts published your message.', 'Messaged Published');
          }
        }
      } else {
        // Update main feed for host users
        if (this.isUserHost(this.user()?.userId ?? '')) {
          if (scMessage.status) {
            // this.feed.update(feed => [...feed, scMessage]);
            if (scMessage.status === ChatStatus.APPROVED) {
              this.feed.update((feed) => [...feed, scMessage]);
            } else if (scMessage.status === ChatStatus.REPLIED) {
              this.updateMainFeedOnReply(scMessage);
            }
          }
        } else {
          // Update main feed for guest users
          if (scMessage.status === ChatStatus.REPLIED) {
            this.updateMainFeedOnReply(scMessage);
          } else if (scMessage.status !== ChatStatus.REJECTED) {
            this.feed.update((feed) => [...feed, scMessage]);
          }
        }
      }
    }
  }

  private updateMessageFeed(
    scMessage: SocialChatMessage,
    feed: SocialChatMessage[] | (() => SocialChatMessage[]),
  ): void {
    const feedArray = feed instanceof Function ? feed() : feed;
    const msgIndex = feedArray.findIndex(
      (msg: SocialChatMessage) =>
        _.isEqual(msg.message, scMessage.message) &&
        _.isEqual(msg.sentTimestamp, scMessage.sourceDate) &&
        _.isEqual(msg.sender.userId, scMessage.sender.userId),
    );

    if (msgIndex >= 0) {
      if (feed instanceof Function) {
        // It's a signal
        const signalFeed = feed as unknown as {
          update: (updaterFn: (value: SocialChatMessage[]) => SocialChatMessage[]) => void;
        };
        signalFeed.update((currentFeed) => {
          const updatedFeed = [...currentFeed];
          updatedFeed.splice(msgIndex, 1, scMessage);
          return updatedFeed;
        });
      } else {
        // It's a regular array
        feed.splice(msgIndex, 1, scMessage);
      }
    }
  }

  public setUserIdToReply(userId?: string, repliedMsg?: SocialChatMessage): void {
    this.userIdToReply = userId;
    this.repliedMsg = repliedMsg;
  }

  // Reorder moderation feed so the replied message, is the last message
  private updateModerationFeedOnReply(repliedMsg: SocialChatMessage): void {
    this.moderationFeed.update((feed) => {
      const msgIndex = feed.findIndex(
        (msg) =>
          _.isEqual(msg.message, repliedMsg.message) &&
          _.isEqual(msg.sentTimestamp, repliedMsg.sentTimestamp) &&
          _.isEqual(msg.sender.userId, repliedMsg.sender.userId),
      );

      if (msgIndex >= 0) {
        const updatedFeed = [...feed];
        const [removedItem] = updatedFeed.splice(msgIndex, 1);
        updatedFeed.push(removedItem);
        return updatedFeed;
      }

      return feed;
    });
  }

  // Reorder main feed of the user that messaged was replied, so his message is above the reply
  private updateMainFeedOnSendByMe(
    repliedMsg: SocialChatMessage,
    feed: SocialChatMessage[] | (() => SocialChatMessage[]),
  ) {
    const feedArray = feed instanceof Function ? feed() : feed;
    const msgIndex = feedArray.findIndex(
      (msg: SocialChatMessage) =>
        _.isEqual(msg.message, repliedMsg.message) &&
        _.isEqual(msg.sentTimestamp, repliedMsg.sentTimestamp) &&
        _.isEqual(msg.sender.userId, repliedMsg.sender.userId),
    );

    if (msgIndex >= 0) {
      if (feed instanceof Function) {
        // It's a signal
        const signalFeed = feed as unknown as {
          update: (updaterFn: (value: SocialChatMessage[]) => SocialChatMessage[]) => void;
        };
        signalFeed.update((currentFeed) => {
          const updatedFeed = [...currentFeed];
          const [removedItem] = updatedFeed.splice(msgIndex, 1);
          const penultimateIndex = updatedFeed.length - 1;
          updatedFeed.splice(penultimateIndex, 0, removedItem);
          return updatedFeed;
        });
      } else {
        // It's a regular array
        const [removedItem] = feed.splice(msgIndex, 1);
        const penultimateIndex = feed.length - 1;
        feed.splice(penultimateIndex, 0, removedItem);
      }
    }
  }

  // Reorder main feed of other users, so the reply is below of the origin message
  private updateMainFeedOnReply(repliedMsg: SocialChatMessage) {
    this.feed.update((feed) => {
      const penultimateIndex = feed.length - 1;

      // Create a new array with the item inserted
      return [
        ...feed.slice(0, penultimateIndex), // All items before the penultimate position
        repliedMsg, // The new item
        ...feed.slice(penultimateIndex), // All items from the penultimate position onward
      ];
    });
  }

  private isUserHost(userId: string): boolean {
    return this.hostIdList?.find((hostId) => hostId === userId) ? true : false;
  }

  private hasMessageAlready(
    message: string,
    feed: SocialChatMessage[] | (() => SocialChatMessage[]),
    sentTimestamp?: number,
    userId?: string,
  ): boolean {
    const feedArray = feed instanceof Function ? feed() : feed;
    const msgIndex = feedArray.findIndex(
      (msg: SocialChatMessage) =>
        _.isEqual(msg.message, message) &&
        _.isEqual(msg.sentTimestamp, sentTimestamp) &&
        _.isEqual(msg.sender.userId, userId),
    );

    return msgIndex >= 0;
  }

  public openUserBadge(user: User, position: { x: number; y: number }): void {
    if (user && user.userId) {
      this.store.dispatch(
        socialAppActions.TOGGLE_BADGE_OPEN({
          open: true,
          userId: user.userId,
          position: position,
          isChat: true,
        }),
      );
    }
  }
}
